"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  type RegexJob,
  type RegexOutcome,
  regexWorkerSource,
  runRegexJob,
} from "@/lib/tool-kit/developer/regex/run";

/*
 * Runs regex jobs in a Web Worker created from a Blob URL, with a time limit.
 *
 * A JavaScript regex match cannot be interrupted once it starts, so a pattern that
 * backtracks catastrophically would freeze the page if it ran here. In the worker it
 * only freezes the worker: when a job overruns, the worker is terminated and a fresh one
 * is created for the next job. Typing is debounced so a burst of keystrokes is one job.
 *
 * If the browser will not start the worker (no Worker support, or a policy that blocks
 * blob: workers), the status becomes "unavailable" and nothing runs until the reader
 * explicitly chooses to run once on the page with `runHere`.
 */

export type RunnerStatus = "done" | "running" | "timeout" | "unavailable";

export interface RunnerState {
  status: RunnerStatus;
  /** The latest finished outcome; kept on screen while the next job runs. */
  outcome: RegexOutcome | null;
}

const DEBOUNCE_MS = 150;

interface WorkerReply {
  id: number;
  outcome: RegexOutcome;
}

export function useRegexRunner(
  job: RegexJob,
  initial: RegexOutcome,
  timeoutMs: number
): RunnerState & { runHere: () => void } {
  const [state, setState] = useState<RunnerState>({
    status: "done",
    outcome: initial,
  });
  const worker = useRef<Worker | null>(null);
  const blobUrl = useRef<string | null>(null);
  const inFlight = useRef<number | null>(null);
  const counter = useRef(0);
  const jobKey = JSON.stringify(job);

  const terminate = useCallback(() => {
    worker.current?.terminate();
    worker.current = null;
    inFlight.current = null;
  }, []);

  const ensureWorker = useCallback((): Worker | null => {
    if (worker.current) {
      return worker.current;
    }
    if (typeof Worker === "undefined") {
      return null;
    }
    try {
      blobUrl.current ??= URL.createObjectURL(
        new Blob([regexWorkerSource()], { type: "text/javascript" })
      );
      worker.current = new Worker(blobUrl.current);
      return worker.current;
    } catch {
      return null;
    }
  }, []);

  useEffect(
    () => () => {
      worker.current?.terminate();
      if (blobUrl.current) {
        URL.revokeObjectURL(blobUrl.current);
      }
    },
    []
  );

  useEffect(() => {
    const request = JSON.parse(jobKey) as RegexJob;
    counter.current += 1;
    const id = counter.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const debounce = setTimeout(() => {
      if (inFlight.current !== null) {
        // The previous job is still running and may never finish: start clean.
        terminate();
      }
      const current = ensureWorker();
      if (current === null) {
        setState({ status: "unavailable", outcome: null });
        return;
      }
      inFlight.current = id;
      setState((previous) => ({ ...previous, status: "running" }));
      timer = setTimeout(() => {
        if (inFlight.current === id) {
          terminate();
          setState({ status: "timeout", outcome: null });
        }
      }, timeoutMs);
      current.onmessage = (event: MessageEvent<WorkerReply>) => {
        if (event.data.id !== id) {
          return;
        }
        clearTimeout(timer);
        inFlight.current = null;
        // A reply to input the reader has since changed is not worth showing.
        if (id === counter.current) {
          setState({ status: "done", outcome: event.data.outcome });
        }
      };
      current.onerror = (event) => {
        event.preventDefault();
        clearTimeout(timer);
        terminate();
        setState({ status: "unavailable", outcome: null });
      };
      current.postMessage({ id, job: request });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(debounce);
      clearTimeout(timer);
    };
  }, [jobKey, timeoutMs, terminate, ensureWorker]);

  const runHere = useCallback(() => {
    setState({
      status: "done",
      outcome: runRegexJob(JSON.parse(jobKey) as RegexJob),
    });
  }, [jobKey]);

  return { ...state, runHere };
}
